import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import type { FieldDescriptor } from '../../types/common';
import type { FieldQuery } from '../../../context/types/store';

import { RadioGroupFieldElement } from './RadioGroupField';
import type { RadioGroupField, APIRadioGroupField } from './types';

const createValidationSchema = (field: RadioGroupField) => {
  let fieldSchema: ZodTypeAny = z.union([z.string(), z.number()], {
    required_error: 'Please select an option',
  });

  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<APIRadioGroupField, RadioGroupField> = {
  FieldComponent: RadioGroupFieldElement,
  name: 'RadioGroupField',
  createValidationSchema,
  parseFieldResponse: ({ field, fieldKey, parse }) => {
    const allNestedFields = [];

    const updatedOptions = field.options.map((option) => {
      if (!option.fields || option.fields.length === 0) {
        const { fields, ...optionWithoutFields } = option;
        return optionWithoutFields;
      }

      const parsedFields = option.fields.map((childField) => parse(childField)).filter(Boolean);

      allNestedFields.push(...parsedFields);

      return {
        ...option,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        fields: parsedFields.map((f) => f![0].key),
      };
    });

    const normalizedField: FieldQuery = {
      key: fieldKey,
      type: field.type,
      data: {
        ...field,
        options: updatedOptions,
      },
    };

    return [normalizedField, allNestedFields];
  },
};

export default Descriptor;

export type { RadioGroupField as FieldType };
